import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Info } from 'lucide-react';
import { IDField } from './IDField';
import { DiscographySourceType, useDiscographyIds } from '../useDiscographyIds';
import { AliasId } from './AliasModal';
import WCheckBox from 'shared_components/components/WCheckBox';
import { WSelect } from 'shared_components/components/WForms/WSelect/WSelect';
import Button from 'shared_components/components/Button';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { usePrimaryDiscographyRun } from '../usePrimaryDiscographyRun';
import { useApi } from 'shared_components/context';
import { useClientId } from 'shared_components/context/client';

export interface BandId {
  source: DiscographySourceType;
  id: string;
  itemId: string;
  originalId?: string;
  validating?: boolean;
  validated?: boolean;
  valid?: boolean;
  approved?: boolean;
  rejected?: boolean;
  error?: string | null;
  artistName?: string;
  originalArtistName?: string;
  editing?: boolean;
  created_by_system: boolean;
}

export type AddGroupStep = 'alias' | 'source_id' | 'form';

export interface BandForm {
  id?: string;
  linkedAliasId?: string | null;
  stageName: string;
  isPermanentMember: boolean;
  isMainPerformer: boolean;
  ids: BandId[];
  manualStageName: boolean;
  hasAutoPopulated: boolean;
  errors: Record<string, string | undefined>;
  bandMode?: 'new' | 'existing';
  selectedExistingBandId?: string;
  /** Add Group journey: step 1 = alias only, step 2 = alias + source id input, step 3 = full form */
  addGroupStep?: AddGroupStep;
  /** Value entered in "Provide Group Source ID" (create mode) */
  groupSourceId?: string;
}

interface BandModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  onClose: () => void;
  formData: BandForm;
  activeTab: string;
  onTabChange: (tab: DiscographySourceType) => void;
  onItemsChange: (updatedItem: AliasId) => void;
  onRemoveItem: (removedItem: AliasId) => void;
  onNameChange: (newName: string) => void;
  onPermanentMemberChange: (value: boolean) => void;
  onLinkedAliasChange: (value: string) => void;
  onAddIdField: () => void;
  onSubmit: () => void;
  onCreateNewAlias: () => void;
  onBandModeChange?: (mode: 'new' | 'existing') => void;
  onExistingBandChange?: (
    bandId: string,
    bandData?: {
      bandId: string;
      name: string;
      sourceId?: string;
      source?: string;
    }
  ) => void;
  /** When source ID lookup finds no band or band not available for artist: parent sets new band form and addGroupStep 'form' */
  onNewBandFromSourceId?: (
    sourceId: string,
    band?: { id: string; stageName: string; sourceIds?: string } | null
  ) => void;
  /** Parent sets groupSourceId and addGroupStep (e.g. 'source_id' when alias selected) */
  onGroupSourceIdChange?: (sourceId: string) => void;
  /** When user skips source ID lookup: parent shows form for creating a new group without pre-filled IDs */
  onSkipSourceIdLookup?: () => void;
}

const BandModal: React.FC<BandModalProps> = ({
  show,
  mode = 'create',
  onClose,
  formData,
  activeTab,
  onTabChange,
  onItemsChange,
  onRemoveItem,
  onNameChange,
  onPermanentMemberChange,
  onLinkedAliasChange,
  onAddIdField,
  onSubmit,
  onCreateNewAlias,
  onBandModeChange,
  onExistingBandChange,
  onNewBandFromSourceId,
  onGroupSourceIdChange,
  onSkipSourceIdLookup,
}) => {
  console.log('=== BandModal render ===');
  console.log('Props received:', {
    show,
    formData: {
      ...formData,
      ids: formData.ids?.length || 0,
    },
    onBandModeChange: !!onBandModeChange,
    onExistingBandChange: !!onExistingBandChange,
    onBandModeChangeType: typeof onBandModeChange,
    onExistingBandChangeType: typeof onExistingBandChange,
  });
  const { collectedDiscographyIds: _unsortedCollectedDiscographyIds } =
    useDiscographyIds();
  const { adminApi: api } = useApi();
  const { clientId } = useClientId();

  const collectedDiscographyIds = [..._unsortedCollectedDiscographyIds].sort(
    (a, b) => {
      return a.stageName.localeCompare(b.stageName);
    }
  );

  const availableAliases = useMemo(() => {
    const aliases = collectedDiscographyIds.filter((a) => a.type === 'persona');
    return aliases.map((alias) => ({
      label: alias.stageName,
      value: alias.id,
    }));
  }, [collectedDiscographyIds]);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [groupSourceIdInput, setGroupSourceIdInput] = useState('');
  const [sourceIdLookupLoading, setSourceIdLookupLoading] = useState(false);
  const [sourceIdLookupError, setSourceIdLookupError] = useState<string | null>(
    null
  );
  const [unlinkedBands, setUnlinkedBands] = useState<
    {
      bandId: string;
      name: string;
      sourceId: string;
      source: string;
    }[]
  >([]);
  const [isLoadingBands, setIsLoadingBands] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Fetch all unlinked bands when linked alias is set (create: for source-id lookup comparison; existing: for dropdown)
  useEffect(() => {
    console.log('formData', formData);
    console.log('clientId', clientId);
    const currentBandMode = formData.bandMode || 'new';
    const isCreateWithAlias = mode === 'create' && !!formData.linkedAliasId;

    // Fetch when: existing mode with alias, or create mode with alias (for source-id lookup comparison)
    if (currentBandMode !== 'existing' && !isCreateWithAlias) {
      setUnlinkedBands([]);
      setSearchQuery(''); // Clear search when mode changes
      return;
    }

    if (!clientId || !formData.linkedAliasId) {
      setUnlinkedBands([]);
      return;
    }

    console.log('fetching unlinked bands');
    // Fetch all unlinked bands for the given alias
    setIsLoadingBands(true);
    api
      .searchUnlinkedBandsUnlinkedBand({
        userId: clientId,
        aliasId: formData.linkedAliasId!,
      })
      .then((response) => {
        console.log('=== API Response for unlinked bands ===');
        console.log('Raw response:', response);
        console.log('Response type:', typeof response);
        console.log('Is array:', Array.isArray(response));

        // Handle response - it might be an array or a single object with an array property
        const bands = Array.isArray(response)
          ? response
          : (response as any).bands || (response as any).results || [];

        console.log('Extracted bands array:', bands);
        console.log('First band structure:', bands[0]);
        if (bands[0]) {
          console.log('First band keys:', Object.keys(bands[0]));
          console.log('First band.bandId:', bands[0].bandId);
          console.log('First band.band_id:', (bands[0] as any).band_id);
        }

        setUnlinkedBands(bands);
      })
      .catch((error) => {
        console.error('Error fetching unlinked bands:', error);
        setUnlinkedBands([]);
      })
      .finally(() => {
        setIsLoadingBands(false);
      });
  }, [formData.linkedAliasId, formData.bandMode, clientId, api, mode]);

  // Filter bands client-side based on search query
  const filteredBands = useMemo(() => {
    // Map bands to display format first
    const mappedBands = unlinkedBands.map((band) => {
      const idsLabel = band.sourceId ? ` (${band.sourceId})` : '';
      // Use band_id if bandId is not available (API might return band_id instead)
      // Also check if the structure is nested (band.band_id)
      const bandIdValue =
        band.bandId || (band as any).band_id || (band as any).band?.band_id;
      console.log('Mapping band:', {
        band,
        bandId: band.bandId,
        band_id: (band as any).band_id,
        nested_band_id: (band as any).band?.band_id,
        bandIdValue,
      });

      if (!bandIdValue) {
        console.error('WARNING: No band ID found for band:', band);
      }

      return {
        label: `${band.name}${idsLabel}`,
        value: bandIdValue,
        band: band,
      };
    });
    console.log('unlinkedBands', unlinkedBands);

    // If no search query (after trimming), return all mapped bands
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      return mappedBands;
    }

    // Filter by matching against the full label (as displayed) using indexOf
    const query = trimmedQuery.toLowerCase();
    console.log('mappedBands', mappedBands);
    return mappedBands.filter(
      (band) =>
        band.label.toLowerCase().indexOf(query) > -1 ||
        band.band.name.toLowerCase().indexOf(query) > -1 ||
        (band.band.sourceId &&
          band.band.sourceId.toLowerCase().indexOf(query) > -1)
    );
  }, [unlinkedBands, searchQuery]);

  // Update search query when a band is selected (to show the selected band's label)
  useEffect(() => {
    console.log('=== useEffect for search query update ===');
    console.log(
      'formData.selectedExistingBandId:',
      formData.selectedExistingBandId
    );
    console.log('formData.stageName:', formData.stageName);
    console.log('formData.ids:', formData.ids);
    console.log('isSearchOpen:', isSearchOpen);
    console.log('filteredBandsCount:', filteredBands.length);

    if (formData.selectedExistingBandId && !isSearchOpen) {
      // Try to find in filteredBands first
      const selected = filteredBands.find(
        (b) => b.value === formData.selectedExistingBandId
      );
      console.log('Selected band in filteredBands:', selected);

      if (selected) {
        console.log('Setting searchQuery to selected label:', selected.label);
        setSearchQuery(selected.label);
      } else if (formData.stageName) {
        // Fallback to stageName from formData if not found in filteredBands
        console.log('Setting searchQuery to stageName:', formData.stageName);
        setSearchQuery(formData.stageName);
      } else {
        console.warn('No selected band found and no stageName available');
      }
    }
  }, [
    formData.selectedExistingBandId,
    formData.stageName,
    formData.ids,
    filteredBands,
    isSearchOpen,
  ]);

  // Log formData changes
  useEffect(() => {
    console.log('=== formData changed ===');
    console.log('formData:', {
      selectedExistingBandId: formData.selectedExistingBandId,
      stageName: formData.stageName,
      bandMode: formData.bandMode,
      idsCount: formData.ids?.length || 0,
      ids: formData.ids,
    });
  }, [
    formData.selectedExistingBandId,
    formData.stageName,
    formData.bandMode,
    formData.ids,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        searchRef.current &&
        !searchRef.current.contains(target) &&
        !document.getElementById('existing-band-listbox')?.contains(target)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update dropdown position when it opens or window scrolls/resizes
  useEffect(() => {
    const updatePosition = () => {
      if (isSearchOpen && searchRef.current) {
        const rect = searchRef.current.getBoundingClientRect();
        // Use getBoundingClientRect() values directly for fixed positioning (viewport-relative)
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      } else {
        setDropdownPosition(null);
      }
    };

    updatePosition();

    if (isSearchOpen) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isSearchOpen]);

  // Clear search query when switching modes
  useEffect(() => {
    const bandMode = formData.bandMode || 'new';
    if (bandMode === 'new') {
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  }, [formData.bandMode]);

  const { actionsDisabled } = usePrimaryDiscographyRun();

  if (!show) return null;

  const showDiscogsMultiplIDsWarning =
    formData.ids.filter((id) => id.source === 'discogs').length > 1;

  const bandMode = formData.bandMode || 'new';
  const addGroupStep = formData.addGroupStep;
  const selectedBand = filteredBands.find(
    (b) => b.value === formData.selectedExistingBandId
  );

  // Determine which inputs should be disabled based on form state
  const hasLinkedAlias = !!formData.linkedAliasId;
  const hasExistingBandSelected =
    bandMode === 'existing' && !!formData.selectedExistingBandId;
  const hasNewGroupType = bandMode === 'new';
  const hasAtLeastOneDiscographyId = formData.ids.some(
    (id) => id.id && id.id.trim() !== ''
  );

  // Disable all inputs until Linked Alias is selected
  const inputsDisabled = !hasLinkedAlias || actionsDisabled;

  // Disable Is Permanent Member and Discography IDs until:
  // - Linked Alias + Existing + Selected Existing Group OR
  // - Linked Alias + New group type selected
  const permanentMemberAndIdsDisabled =
    !hasLinkedAlias ||
    (bandMode === 'existing' && !hasExistingBandSelected) ||
    actionsDisabled;

  // Disable footer until:
  // - Linked Alias + New + at least one discography ID OR
  // - Linked Alias + Existing + existing band + discography ID
  const hasIdError = formData.ids.some((id) => id.error);
  const hasIdValidating = formData.ids.some((id) => id.validating);
  const footerDisabled =
    !hasLinkedAlias ||
    (hasNewGroupType && !hasAtLeastOneDiscographyId) ||
    (bandMode === 'existing' &&
      (!hasExistingBandSelected || !hasAtLeastOneDiscographyId)) ||
    hasIdError ||
    hasIdValidating ||
    actionsDisabled;

  console.log('BandModal render state:', {
    bandMode,
    selectedExistingBandId: formData.selectedExistingBandId,
    selectedBand,
    filteredBandsCount: filteredBands.length,
    unlinkedBandsCount: unlinkedBands.length,
    searchQuery,
    isSearchOpen,
  });

  const handleBandSelect = (
    bandId: string,
    event?: React.MouseEvent | React.KeyboardEvent
  ) => {
    console.log('=== handleBandSelect START ===');
    console.log('bandId:', bandId);
    console.log('event:', event);
    console.log('unlinkedBands:', unlinkedBands);
    console.log('filteredBands:', filteredBands);
    console.log('onExistingBandChange exists:', !!onExistingBandChange);

    // Find the selected band from unlinkedBands to pass its data
    // Check both bandId and band_id since API might return either
    const selectedUnlinkedBand = unlinkedBands.find((b) => {
      const bId = b.bandId || (b as any).band_id;
      return bId === bandId;
    });
    console.log('selectedUnlinkedBand found:', selectedUnlinkedBand);

    // Also check in filteredBands
    const selectedFilteredBand = filteredBands.find((b) => b.value === bandId);
    console.log('selectedFilteredBand found:', selectedFilteredBand);

    // Get the actual band ID to use (prefer bandId, fallback to band_id)
    const actualBandId =
      selectedUnlinkedBand?.bandId ||
      (selectedUnlinkedBand as any)?.band_id ||
      bandId;
    console.log('actualBandId to use:', actualBandId);

    if (onExistingBandChange) {
      console.log('Calling onExistingBandChange with:', {
        bandId: actualBandId,
        selectedUnlinkedBand,
      });
      try {
        onExistingBandChange(actualBandId, selectedUnlinkedBand);
        console.log('onExistingBandChange called successfully');
      } catch (error) {
        console.error('Error calling onExistingBandChange:', error);
      }
    } else {
      console.error('onExistingBandChange is not defined!');
    }

    console.log('Closing dropdown and updating search');
    setIsSearchOpen(false);
    // The useEffect will update searchQuery to show the selected band's label
    console.log('=== handleBandSelect END ===');
  };

  const handleSourceIdLookup = async () => {
    const sourceId = groupSourceIdInput.trim();
    if (!sourceId) {
      setSourceIdLookupError('Please enter a Group Source ID');
      return;
    }
    setSourceIdLookupError(null);
    setSourceIdLookupLoading(true);
    try {
      const band = await api.findBandBySourceIdArtistBand({ sourceId });
      if (band && band.id) {
        const bandIdValue = band.id;
        const isInUnlinked = unlinkedBands.some(
          (b) => (b.bandId || (b as any).band_id) === bandIdValue
        );
        if (isInUnlinked) {
          const unlinkedEntry = unlinkedBands.find(
            (b) => (b.bandId || (b as any).band_id) === bandIdValue
          );
          if (onExistingBandChange) {
            onExistingBandChange(
              bandIdValue,
              unlinkedEntry ?? {
                bandId: bandIdValue,
                name: band.stageName,
                sourceId,
                source: 'discogs',
              }
            );
          }
        } else {
          if (onNewBandFromSourceId) {
            onNewBandFromSourceId(sourceId, band);
          }
        }
      } else {
        if (onNewBandFromSourceId) {
          onNewBandFromSourceId(sourceId, null);
        }
      }
    } catch (_err) {
      setSourceIdLookupError(
        'No group found for this source ID. You can add a new group below.'
      );
      if (onNewBandFromSourceId) {
        onNewBandFromSourceId(sourceId, null);
      }
    } finally {
      setSourceIdLookupLoading(false);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-70 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-gray-800 tw-rounded-lg tw-max-h-[90vh] tw-flex tw-flex-col tw-max-w-xl tw-w-full tw-mx-4 tw-overflow-hidden">
        <div className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-border-b tw-border-gray-700 tw-flex-shrink-0">
          <h3 className="tw-text-white tw-text-lg tw-font-medium">
            {mode === 'create' ? 'Add Group' : 'Update Group'}
          </h3>
          <button
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-white"
          >
            <X size={20} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-hidden"
        >
          <div className="tw-overflow-y-auto tw-p-3 tw-flex-shrink-0">
            {formData.errors.general && (
              <div className="tw-bg-westburyRed/50 tw-border tw-border-westburyRed tw-text-westburyRed tw-p-2 tw-mb-3 tw-rounded-md">
                {formData.errors.general}
              </div>
            )}
            <div className="tw-space-y-3">
              {/* Step 1: Linked alias - always show in create; in create mode step 'alias' this may be the only field */}
              <div>
                <div className="tw-flex tw-gap-2 tw-flex-col tw-items-start tw-justify-start">
                  <label
                    htmlFor="linked_alias_id"
                    className="tw-text-white tw-mb-1 tw-flex tw-items-center tw-gap-3 tw-justify-between tw-w-full"
                  >
                    Linked alias
                    {!actionsDisabled ? (
                      <WButton
                        label="+ Create New Alias"
                        onClick={onCreateNewAlias}
                        variant="primary"
                      />
                    ) : null}
                  </label>
                  <select
                    disabled={actionsDisabled}
                    name="linked_alias_id"
                    placeholder="Select alias"
                    value={formData.linkedAliasId ?? undefined}
                    onChange={(e) => {
                      if (e.target.value) {
                        onLinkedAliasChange(e.target.value);
                        if (onGroupSourceIdChange) onGroupSourceIdChange('');
                        setGroupSourceIdInput('');
                        setSourceIdLookupError(null);
                      }
                    }}
                    className={`tw-w-full tw-bg-gray-700 tw-border ${
                      formData.errors.aliasName
                        ? 'tw-border-westburyRed'
                        : 'tw-border-gray-600'
                    } tw-rounded-md tw-px-2 tw-py-1.5 tw-text-white tw-outline-none ${
                      actionsDisabled
                        ? 'tw-opacity-50 tw-cursor-not-allowed'
                        : ''
                    }`}
                  >
                    <option>Select alias</option>
                    {availableAliases.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {formData.errors.missing_alias_link && (
                    <p className="tw-text-westburyRed tw-text-xs tw-mt-1">
                      {formData.errors.missing_alias_link}
                    </p>
                  )}
                </div>
              </div>
              {/* Step 2 (Add Group): Provide Group Source ID - show after alias selected, until lookup completes */}
              {mode === 'create' &&
                formData.linkedAliasId &&
                addGroupStep !== 'form' && (
                  <div>
                    <label
                      htmlFor="group_source_id"
                      className="tw-text-white tw-mb-1 tw-block"
                    >
                      Provide Discogs or MusicBrainz ID
                    </label>
                    <div className="tw-flex tw-gap-2 tw-items-center">
                      <input
                        id="group_source_id"
                        type="text"
                        disabled={sourceIdLookupLoading || actionsDisabled}
                        placeholder="Discogs or MusicBrainz ID"
                        value={groupSourceIdInput}
                        onChange={(e) => {
                          setGroupSourceIdInput(e.target.value);
                          setSourceIdLookupError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSourceIdLookup();
                          }
                        }}
                        className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded-md tw-px-3 tw-py-1.5 tw-text-white tw-outline-none"
                      />
                      <WButton
                        label={
                          sourceIdLookupLoading ? 'Looking up…' : 'Look up'
                        }
                        onClick={handleSourceIdLookup}
                        variant="primary"
                        disabled={
                          sourceIdLookupLoading ||
                          !groupSourceIdInput.trim() ||
                          actionsDisabled
                        }
                      />
                      <WButton
                        label="Skip"
                        onClick={() => onSkipSourceIdLookup?.()}
                        variant="secondary"
                        disabled={sourceIdLookupLoading || actionsDisabled}
                      />
                    </div>
                    {onSkipSourceIdLookup && (
                      <p className="tw-text-gray-400 tw-text-xs tw-mt-1">
                        Don&apos;t have an ID? Skip to create a new group.
                      </p>
                    )}
                    {sourceIdLookupError && (
                      <p className="tw-text-westburyAmber tw-text-xs tw-mt-1">
                        {sourceIdLookupError}
                      </p>
                    )}
                  </div>
                )}
              {/* Step 3: Full form - show when edit or when create and step is 'form' */}
              {(mode !== 'create' || addGroupStep === 'form') && (
                <>
                  {/* Group Name - show for both new and existing groups (editable) */}
                  <div>
                    <label className="tw-text-white tw-mb-1 tw-flex tw-items-center">
                      Group Name
                      {formData.stageName ? (
                        <span
                          className={`tw-ml-2 tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full ${
                            formData.manualStageName
                              ? 'tw-bg-westburyAmber/50 tw-text-westburyAmber'
                              : 'tw-bg-westburyBlue/50 tw-text-westburyBlue'
                          }`}
                        >
                          {formData.manualStageName
                            ? 'Manually entered'
                            : `Auto-populated`}
                        </span>
                      ) : null}
                    </label>
                    <input
                      disabled={inputsDisabled}
                      type="text"
                      name="stageName"
                      value={formData.stageName}
                      onChange={(e) => onNameChange(e.target.value)}
                      className={`tw-w-full tw-bg-gray-700 tw-border ${
                        formData.errors.aliasName
                          ? 'tw-border-westburyRed'
                          : 'tw-border-gray-600'
                      } tw-rounded-md tw-px-3 tw-py-1.5 tw-text-white tw-outline-none ${
                        inputsDisabled
                          ? 'tw-opacity-50 tw-cursor-not-allowed'
                          : ''
                      }`}
                      required
                    />
                    {formData.errors.aliasName && (
                      <p className="tw-text-westburyRed tw-text-xs tw-mt-1">
                        {formData.errors.aliasName}
                      </p>
                    )}
                  </div>
                  {/* Is Permanent Member? Toggle */}
                  <div className="tw-flex tw-gap-3 tw-flex-col tw-items-start tw-justify-start tw-mt-3">
                    <label
                      htmlFor="isPermanentMember"
                      className="tw-text-white"
                    >
                      Is permanent member?
                    </label>
                    <div className="tw-flex tw-items-center">
                      <button
                        type="button"
                        disabled={permanentMemberAndIdsDisabled}
                        onClick={() => {
                          if (!permanentMemberAndIdsDisabled) {
                            onPermanentMemberChange(true);
                          }
                        }}
                        className={`tw-px-4 tw-py-1.5 tw-rounded-l-md ${
                          permanentMemberAndIdsDisabled
                            ? 'tw-opacity-50 tw-cursor-not-allowed'
                            : ''
                        } ${
                          formData.isPermanentMember
                            ? 'tw-bg-surfaceLime tw-text-black'
                            : `tw-bg-gray-700 ${
                                permanentMemberAndIdsDisabled
                                  ? ''
                                  : 'hover:tw-bg-surfaceLime hover:tw-text-black'
                              }`
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        disabled={permanentMemberAndIdsDisabled}
                        onClick={() => {
                          if (!permanentMemberAndIdsDisabled) {
                            onPermanentMemberChange(false);
                          }
                        }}
                        className={`tw-px-4 tw-py-1.5 tw-rounded-r-md ${
                          permanentMemberAndIdsDisabled
                            ? 'tw-opacity-50 tw-cursor-not-allowed'
                            : ''
                        } ${
                          !formData.isPermanentMember
                            ? 'tw-bg-surfaceLime tw-text-black'
                            : `tw-bg-gray-700 ${
                                permanentMemberAndIdsDisabled
                                  ? ''
                                  : 'hover:tw-bg-surfaceLime hover:tw-text-black'
                              }`
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {/* IDs Section - only when form step (or edit) */}
          {(mode !== 'create' || addGroupStep === 'form') && (
            <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-p-3 tw-border-t tw-border-gray-700 tw-overflow-hidden">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-1">
                <div className="tw-block tw-text-white">Discography IDs</div>
                {!permanentMemberAndIdsDisabled ? (
                  <WButton
                    onClick={onAddIdField}
                    variant="secondary"
                    label="+ Add ID"
                  />
                ) : null}
              </div>
              {/* Tabs for source selection */}
              <div className="tw-flex tw-border-b tw-border-gray-700 tw-mb-2">
                <button
                  type="button"
                  disabled={permanentMemberAndIdsDisabled}
                  onClick={() => {
                    if (!permanentMemberAndIdsDisabled) {
                      onTabChange('discogs');
                    }
                  }}
                  className={`tw-px-3 tw-py-1.5 tw-text-sm ${
                    permanentMemberAndIdsDisabled
                      ? 'tw-opacity-50 tw-cursor-not-allowed'
                      : ''
                  } ${
                    activeTab === 'discogs'
                      ? 'tw-text-westburyBlue tw-border-b-2 tw-border-westburyBlue'
                      : 'tw-text-gray-400'
                  }`}
                >
                  Discogs
                </button>
                <button
                  type="button"
                  disabled={permanentMemberAndIdsDisabled}
                  onClick={() => {
                    if (!permanentMemberAndIdsDisabled) {
                      onTabChange('deezer');
                    }
                  }}
                  className={`tw-px-3 tw-py-1.5 tw-text-sm ${
                    permanentMemberAndIdsDisabled
                      ? 'tw-opacity-50 tw-cursor-not-allowed'
                      : ''
                  } ${
                    activeTab === 'deezer'
                      ? 'tw-text-westburyBlue tw-border-b-2 tw-border-westburyBlue'
                      : 'tw-text-gray-400'
                  }`}
                >
                  Deezer
                </button>
                <button
                  type="button"
                  disabled={permanentMemberAndIdsDisabled}
                  onClick={() => {
                    if (!permanentMemberAndIdsDisabled) {
                      onTabChange('spotify');
                    }
                  }}
                  className={`tw-px-3 tw-py-1.5 tw-text-sm ${
                    permanentMemberAndIdsDisabled
                      ? 'tw-opacity-50 tw-cursor-not-allowed'
                      : ''
                  } ${
                    activeTab === 'spotify'
                      ? 'tw-text-westburyBlue tw-border-b-2 tw-border-westburyBlue'
                      : 'tw-text-gray-400'
                  }`}
                >
                  Spotify
                </button>
                <button
                  type="button"
                  disabled={permanentMemberAndIdsDisabled}
                  onClick={() => {
                    if (!permanentMemberAndIdsDisabled) {
                      onTabChange('musicbrainz');
                    }
                  }}
                  className={`tw-px-3 tw-py-1.5 tw-text-sm ${
                    permanentMemberAndIdsDisabled
                      ? 'tw-opacity-50 tw-cursor-not-allowed'
                      : ''
                  } ${
                    activeTab === 'musicbrainz'
                      ? 'tw-text-westburyBlue tw-border-b-2 tw-border-westburyBlue'
                      : 'tw-text-gray-400'
                  }`}
                >
                  MusicBrainz
                </button>
              </div>
              <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
                {showDiscogsMultiplIDsWarning && activeTab === 'discogs' ? (
                  <div className="tw-bg-westburyBlue/50 tw-border tw-border-westburyBlue tw-text-westburyBlue tw-p-2 tw-mb-3 tw-rounded-md tw-inline-flex tw-gap-2 tw-items-center tw-w-full tw-text-sm">
                    <Info size={16} />
                    <span>{'Discogs ID already linked'}</span>
                  </div>
                ) : null}
                {formData.ids
                  .filter((id) => id.source === activeTab)
                  .map((item) => (
                    <IDField
                      disabled={permanentMemberAndIdsDisabled}
                      key={item.itemId}
                      source={activeTab}
                      items={formData.ids}
                      item={item}
                      allowDelete={
                        formData.ids.filter((id) => id.source === activeTab)
                          .length > 0
                      }
                      onRemove={onRemoveItem}
                      onChange={onItemsChange}
                      validateBandSourceId={
                        (mode !== 'create' || addGroupStep === 'form')
                          ? {
                              mode,
                              currentBandId:
                                formData.id ||
                                formData.selectedExistingBandId ||
                                undefined,
                            }
                          : undefined
                      }
                      excludeFromDuplicateCheckBandId={
                        formData.id || formData.selectedExistingBandId || undefined
                      }
                    />
                  ))}
              </div>
            </div>
          )}
        </form>
        <div className="tw-flex tw-justify-end tw-space-x-3 tw-p-3 tw-border-t tw-border-gray-700 tw-flex-shrink-0">
          {!actionsDisabled ? (
            <>
              <WButton
                onClick={() => {
                  // Reset form and close modal
                  if (onExistingBandChange) {
                    onExistingBandChange('', undefined);
                  }
                  onClose();
                }}
                label="Cancel"
                variant="secondary"
              />
              {(mode !== 'create' || addGroupStep === 'form') && (
                <WButton
                  onClick={onSubmit}
                  variant="primary"
                  label={
                    mode === 'create'
                      ? bandMode === 'existing'
                        ? 'Save'
                        : 'Add'
                      : 'Save'
                  }
                  disabled={footerDisabled}
                />
              )}
            </>
          ) : (
            <WButton onClick={onClose} label="Close" variant="secondary" />
          )}
        </div>
      </div>
    </div>
  );
};

export default BandModal;
